import { useId, useState } from "react";
import { accountStatusLabel } from "./AccountStatusPanel";
import type { AccountTab } from "./portal-navigation";

export type DirectoryAccount = {
  id: string;
  name: string;
  account_number: string;
  kind: string;
  status: string;
  contact_name?: string | null;
  billing_email?: string | null;
};

const accountKind = (kind: string) =>
  kind === "commercial"
    ? "Business"
    : kind === "enterprise"
      ? "Enterprise"
      : kind === "residential"
        ? "Residential"
        : "Other";
const statusValue = (status: string) =>
  status === "draft" ? "pending_activation" : status;

export function CustomerDirectory({
  accounts,
  onAccount,
  onAdd,
  addDisabled = false,
  page = 0,
  hasMore = false,
}: {
  accounts: DirectoryAccount[];
  onAccount: (id: string, tab?: AccountTab) => void;
  onAdd: () => void;
  addDisabled?: boolean;
  page?: number;
  hasMore?: boolean;
}) {
  const id = useId();
  const [query, setQuery] = useState("");
  const [kind, setKind] = useState("");
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState("recent");
  const search = query.trim().toLocaleLowerCase();
  const filtered = accounts.filter(
    (account) =>
      (!kind || account.kind === kind) &&
      (!status || statusValue(account.status) === status) &&
      [
        account.name,
        account.account_number,
        account.contact_name,
        account.billing_email,
      ].some((value) => value?.toLocaleLowerCase().includes(search)),
  );
  if (sort === "name")
    filtered.sort(
      (a, b) =>
        a.name.localeCompare(b.name, "en", { sensitivity: "base" }) ||
        a.account_number.localeCompare(b.account_number),
    );
  const narrowed = Boolean(search || kind || status);
  return (
    <section className="cr-directory" aria-label="Customer directory">
      <div className="cr-directory-heading">
        <p>Find a customer, then open the task you need.</p>
        <button className="cr-button" onClick={onAdd} disabled={addDisabled}>
          Add a customer
        </button>
      </div>
      <div className="cr-directory-filters">
        <label className="cr-field cr-directory-search">
          <span>Find a customer</span>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Name, account, contact or email"
            aria-describedby={`${id}-scope`}
          />
        </label>
        <label className="cr-field">
          <span>Customer type</span>
          <select
            value={kind}
            onChange={(event) => setKind(event.target.value)}
          >
            <option value="">All types</option>
            <option value="residential">Residential</option>
            <option value="commercial">Business</option>
            <option value="enterprise">Enterprise</option>
          </select>
        </label>
        <label className="cr-field">
          <span>Account status</span>
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          >
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="pending_activation">Pending Activation</option>
            <option value="suspended">Suspended</option>
            <option value="pending_disconnect">Pending Disconnection</option>
            <option value="disconnected">Disconnected</option>
          </select>
        </label>
        <label className="cr-field">
          <span>Sort customers</span>
          <select
            value={sort}
            onChange={(event) => setSort(event.target.value)}
          >
            <option value="recent">Recently added</option>
            <option value="name">Name, A to Z</option>
          </select>
        </label>
      </div>
      <div className="cr-directory-summary">
        <p id={`${id}-scope`} className="cr-form-note" role="status">
          Showing {filtered.length} of {accounts.length} customers
          {page > 0 || hasMore ? ` on page ${page + 1}` : ""}.
          {page > 0 || hasMore
            ? " Search, filters and sorting apply to this page. Use the page controls below for other records."
            : " Search by name, account number, contact or email."}
        </p>
        {narrowed && (
          <button
            className="cr-text-button"
            onClick={() => {
              setQuery("");
              setKind("");
              setStatus("");
            }}
          >
            Clear filters
          </button>
        )}
      </div>
      {filtered.length ? (
        <div
          className="cr-table-wrap"
          tabIndex={0}
          role="region"
          aria-label="Customer records"
        >
          <table className="cr-directory-table">
            <thead>
              <tr>
                <th scope="col">Customer</th>
                <th scope="col">Type and status</th>
                <th scope="col">Contact</th>
                <th scope="col">Open a task</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((account) => (
                <tr key={account.id}>
                  <td data-label="Customer">
                    <button
                      className="cr-text-button cr-directory-name"
                      onClick={() => onAccount(account.id)}
                    >
                      {account.name}
                    </button>
                    <span className="cr-directory-detail">
                      {account.account_number}
                    </span>
                  </td>
                  <td data-label="Type and status">
                    <span>{accountKind(account.kind)}</span>
                    <span
                      className={`cr-status cr-directory-status ${statusValue(account.status)}`}
                    >
                      {accountStatusLabel(account.status)}
                    </span>
                  </td>
                  <td data-label="Contact">
                    <span>
                      {account.contact_name || "Contact not recorded"}
                    </span>
                    {account.billing_email && (
                      <span className="cr-directory-detail">
                        {account.billing_email}
                      </span>
                    )}
                  </td>
                  <td data-label="Open a task">
                    <div
                      className="cr-directory-tasks"
                      aria-label={`Tasks for ${account.name}`}
                    >
                      <button
                        className="cr-text-button"
                        onClick={() => onAccount(account.id, "numbers")}
                      >
                        Numbers
                      </button>
                      <button
                        className="cr-text-button"
                        onClick={() => onAccount(account.id, "devices")}
                      >
                        Equipment
                      </button>
                      <button
                        className="cr-text-button"
                        onClick={() => onAccount(account.id, "voicemail")}
                      >
                        Voicemail
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="cr-empty">
          <h2>
            {narrowed
              ? "No customers match these filters"
              : "No customers on this page"}
          </h2>
          <p>
            {narrowed
              ? "Change the search or clear the filters to see the other customer records."
              : page > 0
                ? "Use Previous page to return to earlier customer records."
                : "Add a customer to record their contact details and begin telephone setup."}
          </p>
        </div>
      )}
    </section>
  );
}
